/**
 * Moz Target Normalization (Story 20.7, AC #1)
 *
 * `normalizeMozTarget` accepts the customer's raw input (a domain or URL) and
 * returns a canonical lowercase form suitable for Moz V3 `data.site.metrics.fetch`
 * and friends (`params.data.target`). Validation mirrors the SSRF-safe rules
 * from PageSpeed + Ahrefs 20.1 + Semrush 20.4:
 *
 *  - Strip `http(s)://` if present
 *  - Lowercase the hostname
 *  - Strip trailing slash
 *  - Reject localhost / private IP ranges / `.local` / `metadata.google.internal`
 *  - Reject IPv6 literals
 *  - Reject non-standard ports
 *
 * **Round 4 Red Team Attack #2 — CSV-injection prevention:** also reject
 * hostnames containing leading `=`, `+`, `@`, `-`, or tab/CR characters. When
 * downstream agency users export the data to Excel/Google Sheets, those
 * characters trigger formula evaluation. Per OWASP CSV injection cheat sheet
 * — defense at input boundary so the data is never written to KV/D1 in a
 * shape an export pipeline could weaponize.
 *
 * **Why normalization matters for Moz specifically:** Moz V3 returns a
 * `result.site.metrics.{title, subdomain, root_domain}` echo for what target
 * you queried. Inconsistent target casing (e.g. `Example.com` vs `example.com`)
 * creates apparent metric divergence — same root domain, different cache key,
 * different snapshot rows. Canonicalizing at input boundary keeps the
 * `moz_authority_snapshots` dedup contract honest.
 */

import { sanitizeErrorMessage } from "../safe-fetch";

// ============================================================================
// Types
// ============================================================================

export class MozTargetValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "MozTargetValidationError";
  }
}

// ============================================================================
// SSRF-safe hostname rules
// ============================================================================

function isBlockedHostname(host: string): boolean {
  const h = host.toLowerCase();
  if (h === "localhost" || h === "127.0.0.1" || h === "::1") return true;
  if (/^(10\.|172\.(1[6-9]|2\d|3[01])\.|192\.168\.|169\.254\.)/.test(h)) return true;
  if (h.endsWith(".local") || h.endsWith(".internal") || h.endsWith(".svc.cluster.local")) {
    return true;
  }
  if (h === "metadata.google.internal") return true;
  if (h.startsWith("[")) return true; // IPv6 literal in URL form
  return false;
}

/**
 * CSV-injection guard (Round 4 Red Team Attack #2).
 *
 * Reject hostnames or paths that begin with characters Excel/Google Sheets
 * interprets as formula triggers. Real-world hostnames never legitimately
 * begin with these characters, so this is a pure-defense filter with no
 * false-positive risk.
 */
function startsWithFormulaTrigger(value: string): boolean {
  if (value.length === 0) return false;
  const first = value.charCodeAt(0);
  // = + @ - 0x09 (tab) 0x0d (CR)
  return (
    first === 0x3d ||
    first === 0x2b ||
    first === 0x40 ||
    first === 0x2d ||
    first === 0x09 ||
    first === 0x0d
  );
}

// ============================================================================
// Moz scope enum (AC #1 + AC #4)
// ============================================================================

/**
 * The three valid Moz scope values per moz.com/help/links-api/making-calls/url-metrics
 * verified 2026-05-10. Anything else is rejected at the helper boundary
 * (mandatory `scope` parameter per AC #4).
 */
export const MOZ_SCOPE_VALUES = ["page", "subdomain", "root_domain"] as const;
export type MozScope = (typeof MOZ_SCOPE_VALUES)[number];

export function isMozScope(value: unknown): value is MozScope {
  return (
    value === "page" || value === "subdomain" || value === "root_domain"
  );
}

// ============================================================================
// Normalization (AC #1)
// ============================================================================

/**
 * Normalize a raw customer-supplied target (domain or URL) into the canonical
 * form Moz expects.
 *
 * @param input - Raw user input (e.g. "Example.com", "https://example.com/").
 * @returns Canonical form: lowercase hostname, trailing slash stripped, path
 *   preserved when present.
 * @throws MozTargetValidationError if the input is empty, malformed, matches
 *   an SSRF-forbidden hostname, or starts with a CSV-injection formula trigger.
 */
export function normalizeMozTarget(input: string): string {
  if (typeof input !== "string") {
    throw new MozTargetValidationError("Target must be a string.");
  }
  // CSV-injection check on RAW input BEFORE trim() — `.trim()` strips tab/CR
  // characters which would otherwise bypass this guard. Leading `=`, `+`,
  // `@`, `-` are non-whitespace so .trim() preserves them; including them
  // here too keeps the check uniform across all formula-trigger forms.
  if (input.length > 0 && startsWithFormulaTrigger(input)) {
    throw new MozTargetValidationError(
      "Target cannot start with =, +, @, -, tab, or carriage return — these characters are reserved for spreadsheet-export safety.",
    );
  }
  const trimmed = input.trim();
  if (trimmed.length === 0) {
    throw new MozTargetValidationError("Target cannot be empty.");
  }
  if (trimmed.length > 2048) {
    throw new MozTargetValidationError("Target must not exceed 2048 characters.");
  }
  // Defense-in-depth — the trimmed value's first non-whitespace char should
  // never be a formula trigger either.
  if (startsWithFormulaTrigger(trimmed)) {
    throw new MozTargetValidationError(
      "Target cannot start with =, +, @, -, tab, or carriage return — these characters are reserved for spreadsheet-export safety.",
    );
  }

  const wasBare = !/^https?:\/\//i.test(trimmed);
  const candidate = wasBare ? `https://${trimmed}` : trimmed;

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    throw new MozTargetValidationError(
      `Invalid target: ${sanitizeErrorMessage(trimmed)}`,
    );
  }

  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    throw new MozTargetValidationError("Target protocol must be http or https.");
  }

  const hostname = parsed.hostname.toLowerCase();
  if (hostname.length === 0 || !hostname.includes(".")) {
    throw new MozTargetValidationError("Target must include a top-level domain.");
  }
  if (isBlockedHostname(hostname)) {
    throw new MozTargetValidationError(
      "Target points to a private, internal, or metadata host.",
    );
  }
  if (startsWithFormulaTrigger(hostname)) {
    throw new MozTargetValidationError(
      "Target hostname cannot start with =, +, @, -, tab, or carriage return.",
    );
  }
  if (parsed.port && parsed.port !== "80" && parsed.port !== "443") {
    throw new MozTargetValidationError(
      "Target uses a non-standard port — Moz only indexes default-port hosts.",
    );
  }

  // **Story 20.7 AC #1**: strip http(s):// — Moz V3's `target` param accepts
  // bare host with optional path (mirrors Semrush 20.4; DIVERGENT from Ahrefs
  // 20.1 which preserves the protocol).
  const path = parsed.pathname === "/" ? "" : parsed.pathname;
  let normalized = `${hostname}${path}${parsed.search}`;
  if (normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }
  return normalized;
}
